import { Socket } from 'socket.io-client';
import {
  HANDSHAKE_EVENTS,
  PROTOCOL_VERSION,
  HANDSHAKE_CLIENT_OK,
  HANDSHAKE_SERVER_OK,
  DirectionalKeys,
  EncryptedEnvelope,
  computeSharedSecret,
  decodeBase64,
  deriveDirectionalKeys,
  encrypt,
  encryptPayload,
  exportUncompressedPublicKey,
  generateEphemeralKeyPair,
  importSigningPublicKey,
  importUncompressedPublicKey,
  isEncryptedEnvelope,
  decrypt,
  decryptPayload,
  randomNonce4,
  verifyHandshakeSignature,
  encodeBase64,
} from '../utils/socket-encryption.util';

const HANDSHAKE_EVENT_SET = new Set<string>(Object.values(HANDSHAKE_EVENTS));
const SOCKET_IO_SYSTEM_EVENTS = new Set([
  'connect',
  'disconnect',
  'connect_error',
  'error',
  'reconnect',
  'reconnect_attempt',
  'reconnect_error',
  'reconnect_failed',
  'ping',
  'pong',
]);
const HANDSHAKE_TIMEOUT_MS = 30000;

interface SocketEncryptionConfig {
  enabled: boolean;
  signingPublicKeyPem: string;
}

const DEFAULT_SOCKET_E2E_CONFIG: SocketEncryptionConfig = {
  enabled: false,
  signingPublicKeyPem: '',
};

interface PatchedSocket extends Socket {
  __aaOriginalEmit?: Socket['emit'];
  __aaOriginalOn?: Socket['on'];
  __aaOriginalOnevent?: (packet: { data?: unknown[] }) => void;
  __aaEncryptionAttached?: boolean;
}

interface PendingOutboundEmit {
  event: string;
  args: unknown[];
}

interface SocketIoInternal {
  onevent?: (packet: { data?: unknown[] }) => void;
}

class SocketEncryptionService {
  private config: SocketEncryptionConfig = { ...DEFAULT_SOCKET_E2E_CONFIG };
  private secure = false;
  private keys: DirectionalKeys | null = null;
  private c2sSeq = 0;
  private s2cSeq = 0;
  private signingPublicKey: CryptoKey | null = null;
  private handshakePromise: Promise<void> | null = null;
  private inboundChain: Promise<void> = Promise.resolve();
  private pendingOutboundEmits: PendingOutboundEmit[] = [];
  private outboundFlushPromise: Promise<void> | null = null;
  private channelGeneration = 0;

  configure(config: Partial<SocketEncryptionConfig>): void {
    this.config = {
      enabled: config.enabled ?? DEFAULT_SOCKET_E2E_CONFIG.enabled,
      signingPublicKeyPem: config.signingPublicKeyPem ?? DEFAULT_SOCKET_E2E_CONFIG.signingPublicKeyPem,
    };
    this.signingPublicKey = null;
  }

  isEnabled(): boolean {
    return this.config.enabled === true;
  }

  isSecure(): boolean {
    return this.secure;
  }

  attachSocket(socket: PatchedSocket): void {
    if (!this.isEnabled() || socket.__aaEncryptionAttached) {
      return;
    }

    socket.__aaOriginalEmit = socket.emit.bind(socket);
    socket.__aaOriginalOn = socket.on.bind(socket);
    socket.__aaEncryptionAttached = true;

    socket.emit = ((event: string, ...args: unknown[]) => {
      return this.emitThrough(socket, event, ...args);
    }) as Socket['emit'];

    this.patchInboundTransport(socket);

    socket.__aaOriginalOn!('disconnect', () => {
      this.resetState();
    });
  }

  private patchInboundTransport(socket: PatchedSocket): void {
    const socketInternal = socket as unknown as SocketIoInternal;
    if (socket.__aaOriginalOnevent) {
      return;
    }
    socket.__aaOriginalOnevent = socketInternal.onevent?.bind(socket);
    const originalOnevent = socket.__aaOriginalOnevent;
    if (!originalOnevent) {
      return;
    }

    socketInternal.onevent = (packet) => {
      const data = packet?.data;
      if (!Array.isArray(data) || data.length < 2 || !this.shouldDecryptInbound(String(data[0]))) {
        originalOnevent(packet);
        return;
      }

      const eventName = String(data[0]);
      const payload = data[1];
      if (!isEncryptedEnvelope(payload)) {
        this.failSecureChannel(socket, `Plaintext payload rejected for event ${eventName}`);
        return;
      }

      void this.consumeInboundEnvelope(eventName, payload)
        .then((plain) => {
          data[1] = plain;
          originalOnevent(packet);
        })
        .catch((err) => {
          this.failSecureChannel(socket, err?.message || 'Inbound decrypt failed');
        });
    };
  }

  private shouldDecryptInbound(eventName: string): boolean {
    return (
      this.isEnabled() &&
      this.secure &&
      !!this.keys &&
      !HANDSHAKE_EVENT_SET.has(eventName) &&
      !SOCKET_IO_SYSTEM_EVENTS.has(eventName)
    );
  }

  private consumeInboundEnvelope(eventName: string, envelope: EncryptedEnvelope): Promise<unknown> {
    const task = this.inboundChain.then(async () => {
      if (!this.keys) {
        throw new Error('Secure channel keys are not available');
      }
      if (envelope.seq !== this.s2cSeq) {
        throw new Error(
          `Invalid inbound sequence for ${eventName}: expected ${this.s2cSeq}, got ${envelope.seq}`
        );
      }
      const plain = await decryptPayload(this.keys.k_s2c, envelope, envelope.seq);
      this.s2cSeq += 1;
      return plain;
    });
    this.inboundChain = task.then(
      () => undefined,
      () => undefined
    );
    return task;
  }

  private queueOutboundEmit(socket: PatchedSocket, event: string, args: unknown[]): void {
    this.pendingOutboundEmits.push({ event, args });
    this.flushPendingOutbound(socket);
  }

  private flushPendingOutbound(socket: PatchedSocket): void {
    if (
      this.outboundFlushPromise ||
      !socket.connected ||
      !this.secure ||
      !this.keys ||
      this.pendingOutboundEmits.length === 0
    ) {
      return;
    }

    let canRetry = false;
    const flushPromise = this.drainPendingOutbound(socket)
      .then((result) => {
        canRetry = result;
      })
      .catch((err) => {
        console.error('[AA Socket E2E] Unexpected outbound queue failure', err);
      })
      .finally(() => {
        if (this.outboundFlushPromise === flushPromise) {
          this.outboundFlushPromise = null;
        }
        if (
          canRetry &&
          socket.connected &&
          this.secure &&
          this.keys &&
          this.pendingOutboundEmits.length > 0
        ) {
          this.flushPendingOutbound(socket);
        }
      });
    this.outboundFlushPromise = flushPromise;
  }

  private async drainPendingOutbound(socket: PatchedSocket): Promise<boolean> {
    while (
      socket.connected &&
      this.secure &&
      this.keys &&
      this.pendingOutboundEmits.length > 0
    ) {
      const pending = this.pendingOutboundEmits[0];
      const generation = this.channelGeneration;
      const keys = this.keys;
      const seq = this.c2sSeq;
      const payload = pending.args.length <= 1 ? pending.args[0] : pending.args;

      let envelope: EncryptedEnvelope;
      try {
        envelope = await encryptPayload(keys.k_c2s, payload, seq);
      } catch (err) {
        console.error(`[AA Socket E2E] Outbound encrypt failed for ${pending.event}`, err);
        return false;
      }

      if (
        generation !== this.channelGeneration ||
        !socket.connected ||
        !this.secure ||
        this.keys !== keys
      ) {
        return true;
      }

      try {
        socket.__aaOriginalEmit!(pending.event, envelope);
      } catch (err) {
        console.error(`[AA Socket E2E] Outbound emit failed for ${pending.event}`, err);
        return false;
      }
      this.pendingOutboundEmits.shift();
      this.c2sSeq += 1;
    }
    return true;
  }

  private failSecureChannel(socket: PatchedSocket, reason: string): void {
    console.error('[AA Socket E2E]', reason);
    this.resetState();
    socket.disconnect();
  }

  async performHandshake(socket: PatchedSocket): Promise<void> {
    if (!this.isEnabled()) {
      return;
    }
    if (this.handshakePromise) {
      return this.handshakePromise;
    }

    const handshakePromise = this.runHandshake(socket).finally(() => {
      if (this.handshakePromise === handshakePromise) {
        this.handshakePromise = null;
      }
    });
    this.handshakePromise = handshakePromise;
    return handshakePromise;
  }

  private async runHandshake(socket: PatchedSocket): Promise<void> {
    if (!this.config.signingPublicKeyPem?.trim()) {
      throw new Error('Socket E2E signing public key is not configured');
    }

    this.resetState();
    const generation = this.channelGeneration;

    const signingKey = await this.getSigningPublicKey();
    this.assertHandshakeActive(socket, generation);
    const clientKeyPair = await generateEphemeralKeyPair();
    this.assertHandshakeActive(socket, generation);
    const c2sNonce = randomNonce4();
    const clientPublicKey = await exportUncompressedPublicKey(clientKeyPair.publicKey);
    this.assertHandshakeActive(socket, generation);

    const responsePromise = this.waitForHandshakeEvent<{
      serverPublicKey: string;
      s2cNonce: string;
      sig: string;
    }>(socket, HANDSHAKE_EVENTS.RESPONSE, 'key_exchange_response');

    const ackPromise = this.waitForHandshakeEvent<EncryptedEnvelope & { type?: string }>(
      socket,
      HANDSHAKE_EVENTS.ACK,
      'key_exchange_ack'
    );
    void ackPromise.catch(() => undefined);

    socket.__aaOriginalEmit!(HANDSHAKE_EVENTS.INIT, {
      version: PROTOCOL_VERSION,
      type: HANDSHAKE_EVENTS.INIT,
      clientPublicKey: encodeBase64(clientPublicKey),
      c2sNonce: encodeBase64(c2sNonce),
    });

    const response = await responsePromise;
    this.assertHandshakeActive(socket, generation);
    const serverPublicKey = await importUncompressedPublicKey(decodeBase64(response.serverPublicKey));
    const serverPublicKeyBytes = decodeBase64(response.serverPublicKey);
    const s2cNonce = decodeBase64(response.s2cNonce);

    const signatureValid = await verifyHandshakeSignature(
      response.sig,
      clientPublicKey,
      serverPublicKeyBytes,
      c2sNonce,
      s2cNonce,
      socket.id || '',
      signingKey
    );
    if (!signatureValid) {
      throw new Error('Handshake signature verification failed');
    }

    const sharedSecret = await computeSharedSecret(clientKeyPair.privateKey, serverPublicKey);
    this.assertHandshakeActive(socket, generation);
    this.keys = await deriveDirectionalKeys(sharedSecret, c2sNonce, s2cNonce);
    this.assertHandshakeActive(socket, generation);

    const completePayload = await encrypt(this.keys.k_c2s, HANDSHAKE_CLIENT_OK, 0);
    this.assertHandshakeActive(socket, generation);
    socket.__aaOriginalEmit!(HANDSHAKE_EVENTS.COMPLETE, {
      type: HANDSHAKE_EVENTS.COMPLETE,
      encrypted: true,
      seq: 0,
      iv: completePayload.iv,
      ciphertext: completePayload.ciphertext,
      tag: completePayload.tag,
    });

    const ack = await ackPromise;
    this.assertHandshakeActive(socket, generation);
    const serverToken = await decrypt(this.keys.k_s2c, ack, 0);
    this.assertHandshakeActive(socket, generation);
    if (serverToken !== HANDSHAKE_SERVER_OK) {
      throw new Error('Invalid handshake server verification payload');
    }

    this.c2sSeq = 1;
    this.s2cSeq = 1;
    this.secure = true;
    this.flushPendingOutbound(socket);
  }

  private emitThrough(socket: PatchedSocket, event: string, ...args: unknown[]): Socket {
    const originalEmit = socket.__aaOriginalEmit;
    if (!originalEmit || !this.isEnabled()) {
      return originalEmit ? (originalEmit(event, ...args) as Socket) : socket;
    }
    if (HANDSHAKE_EVENT_SET.has(event)) {
      return originalEmit(event, ...args) as Socket;
    }

    this.queueOutboundEmit(socket, event, args);
    return socket;
  }

  private waitForHandshakeEvent<T>(socket: PatchedSocket, event: string, step = event): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const onResponse = (data: T) => {
        cleanup();
        resolve(data);
      };
      const onError = (data: { reason?: string }) => {
        cleanup();
        reject(new Error(data?.reason || 'Handshake failed'));
      };
      const onDisconnect = () => {
        cleanup();
        reject(new Error('Socket disconnected during secure handshake'));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off(event, onResponse as (...args: unknown[]) => void);
        socket.off(HANDSHAKE_EVENTS.ERROR, onError as (...args: unknown[]) => void);
        socket.off('disconnect', onDisconnect);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error(`Handshake timed out waiting for ${step}`));
      }, HANDSHAKE_TIMEOUT_MS);

      socket.__aaOriginalOn!(HANDSHAKE_EVENTS.ERROR, onError);
      socket.__aaOriginalOn!('disconnect', onDisconnect);
      socket.__aaOriginalOn!(event, onResponse);
    });
  }

  private assertHandshakeActive(socket: PatchedSocket, generation: number): void {
    if (!socket.connected || generation !== this.channelGeneration) {
      throw new Error('Socket disconnected during secure handshake');
    }
  }

  private async getSigningPublicKey(): Promise<CryptoKey> {
    if (!this.signingPublicKey) {
      this.signingPublicKey = await importSigningPublicKey(this.config.signingPublicKeyPem);
    }
    return this.signingPublicKey;
  }

  private resetState(): void {
    this.channelGeneration += 1;
    this.secure = false;
    this.keys = null;
    this.c2sSeq = 0;
    this.s2cSeq = 0;
    this.inboundChain = Promise.resolve();
  }
}

export default SocketEncryptionService;
